<template>
  <el-dialog
    v-model="visible"
    title="일정 상세 · 진행 이력"
    :width="isMobile ? undefined : '480px'"
    :fullscreen="isMobile"
  >
    <div v-if="localTask" class="td-head">
      <!-- 보기 -->
      <template v-if="!editing">
        <div class="td-title">{{ localTask.title || '(제목 없음)' }}</div>
        <div class="td-meta">
          <el-tag size="small" :type="statusType">{{ statusLabel }}</el-tag>
          <span class="td-date">{{ localTask.end_date }}</span>
          <el-button text type="primary" size="small" @click="startEdit">수정</el-button>
        </div>
        <div v-if="localTask.description" class="td-desc">{{ localTask.description }}</div>
      </template>
      <!-- 수정 -->
      <template v-else>
        <el-form label-position="top" size="small" class="td-edit-form">
          <el-form-item label="제목">
            <el-input v-model="form.title" maxlength="200" />
          </el-form-item>
          <el-form-item label="마감일">
            <el-date-picker v-model="form.end_date" type="date" value-format="YYYY-MM-DD" style="width: 100%" />
          </el-form-item>
          <el-form-item label="설명">
            <el-input v-model="form.description" type="textarea" :rows="2" />
          </el-form-item>
        </el-form>
        <div class="td-edit-btns">
          <el-button size="small" @click="editing = false">취소</el-button>
          <el-button size="small" type="primary" :loading="savingEdit" @click="saveEdit">저장</el-button>
        </div>
      </template>
    </div>

    <!-- 진행 기록 추가 -->
    <div class="td-add">
      <el-input
        v-model="newNote"
        type="textarea"
        :rows="2"
        maxlength="1000"
        placeholder="진행 내용을 기록하세요 (예: 거래처 통화함 / 자료 발송 완료)"
      />
      <div class="td-add-btn">
        <el-button type="primary" :loading="saving" @click="addLog">기록 추가</el-button>
      </div>
    </div>

    <!-- 타임라인 -->
    <div v-loading="loading" class="td-timeline">
      <el-timeline v-if="logs.length">
        <el-timeline-item
          v-for="log in logs"
          :key="log.id"
          :timestamp="formatTs(log.created_at) + (log.author ? ' · ' + log.author : '')"
          placement="top"
        >
          <div class="tl-note">{{ log.note }}</div>
        </el-timeline-item>
      </el-timeline>
      <el-empty v-else :image-size="60" description="아직 진행 기록이 없습니다" />
    </div>

    <template #footer>
      <div class="td-footer">
        <el-button text type="danger" @click="onDelete">삭제</el-button>
        <span class="td-footer-right">
          <el-button v-if="task && task.status === 'completed'" @click="onToggle">되돌리기</el-button>
          <el-button v-else type="success" @click="onToggle">완료</el-button>
          <el-button type="primary" @click="visible = false">닫기</el-button>
        </span>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import axiosInstance from '@/api/axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import dayjs from 'dayjs'

const props = defineProps({
  modelValue: Boolean,
  task: { type: Object, default: null },
})
const emit = defineEmits(['update:modelValue', 'changed'])

const visible = ref(false)
const loading = ref(false)
const saving = ref(false)
const logs = ref([])
const newNote = ref('')
const isMobile = ref(window.innerWidth <= 768)

const localTask = ref(null)
const editing = ref(false)
const savingEdit = ref(false)
const form = ref({ title: '', end_date: '', description: '' })

const STATUS_LABEL = { pending: '대기', in_progress: '진행 중', completed: '완료' }
const STATUS_TYPE = { pending: 'info', in_progress: 'warning', completed: 'success' }
const statusLabel = computed(() => STATUS_LABEL[localTask.value?.status] || localTask.value?.status || '')
const statusType = computed(() => STATUS_TYPE[localTask.value?.status] || 'info')

const startEdit = () => {
  form.value = {
    title: localTask.value?.title || '',
    end_date: localTask.value?.end_date || '',
    description: localTask.value?.description || '',
  }
  editing.value = true
}

const saveEdit = async () => {
  const title = (form.value.title || '').trim()
  if (!title) {
    ElMessage.warning('제목을 입력하세요.')
    return
  }
  const due = form.value.end_date
  savingEdit.value = true
  try {
    await axiosInstance.patch(`tasks/${localTask.value.id}/`, {
      title,
      start_date: due,
      end_date: due,
      description: form.value.description || '',
    })
    localTask.value = {
      ...localTask.value,
      title,
      start_date: due,
      end_date: due,
      description: form.value.description || '',
    }
    editing.value = false
    ElMessage.success('수정되었습니다.')
    emit('changed')
  } catch (err) {
    console.error('수정 실패:', err)
    ElMessage.error('수정에 실패했습니다.')
  } finally {
    savingEdit.value = false
  }
}

const formatTs = (ts) => (ts ? dayjs(ts).format('YYYY-MM-DD HH:mm') : '')

const fetchLogs = async () => {
  if (!props.task) return
  loading.value = true
  try {
    const res = await axiosInstance.get(`tasks/${props.task.id}/logs/`)
    logs.value = Array.isArray(res.data) ? res.data : res.data?.results || []
  } catch (err) {
    console.error('진행 이력 조회 실패:', err)
    ElMessage.error('진행 이력을 불러오지 못했습니다.')
  } finally {
    loading.value = false
  }
}

const addLog = async () => {
  const note = newNote.value.trim()
  if (!note) {
    ElMessage.warning('진행 내용을 입력하세요.')
    return
  }
  saving.value = true
  try {
    await axiosInstance.post(`tasks/${props.task.id}/logs/`, { note })
    newNote.value = ''
    await fetchLogs()
  } catch (err) {
    console.error('진행 이력 추가 실패:', err)
    ElMessage.error('기록에 실패했습니다.')
  } finally {
    saving.value = false
  }
}

const onToggle = async () => {
  if (!props.task) return
  const next = props.task.status === 'completed' ? 'pending' : 'completed'
  try {
    await axiosInstance.patch(`tasks/${props.task.id}/`, { status: next })
    ElMessage.success(next === 'completed' ? '완료했습니다.' : '되돌렸습니다.')
    emit('changed')
    visible.value = false
  } catch (err) {
    console.error('상태 변경 실패:', err)
    ElMessage.error('처리에 실패했습니다.')
  }
}

const onDelete = async () => {
  if (!props.task) return
  try {
    await ElMessageBox.confirm(`"${props.task.title || '(제목 없음)'}" 을(를) 삭제하시겠습니까?`, '삭제 확인', {
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
      type: 'warning',
    })
  } catch {
    return
  }
  try {
    await axiosInstance.delete(`tasks/${props.task.id}/`)
    emit('changed')
    visible.value = false
  } catch (err) {
    console.error('삭제 실패:', err)
    ElMessage.error('삭제에 실패했습니다.')
  }
}

watch(
  () => props.modelValue,
  (v) => {
    visible.value = v
    if (v && props.task) {
      localTask.value = { ...props.task }
      editing.value = false
      newNote.value = ''
      logs.value = []
      fetchLogs()
    }
  }
)
watch(visible, (v) => emit('update:modelValue', v))
</script>

<style scoped>
.td-head {
  margin-bottom: 12px;
}
.td-title {
  font-size: 16px;
  font-weight: 700;
  color: #303133;
  word-break: break-word;
}
.td-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.td-date {
  font-size: 12px;
  color: #909399;
}
.td-desc {
  margin-top: 6px;
  font-size: 13px;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-word;
}
.td-edit-btns {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 8px;
}

.td-add {
  margin-bottom: 14px;
}
.td-add-btn {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}

.td-timeline {
  max-height: 320px;
  overflow-y: auto;
}
.tl-note {
  white-space: pre-wrap;
  word-break: break-word;
  color: #303133;
}

.td-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.td-footer-right {
  display: flex;
  gap: 8px;
}
</style>
